import { useCallback, useEffect, useMemo, useState } from "react";
import { AttributeType } from "@evergis/api";
import { useTranslation } from "react-i18next";

import { example as pointExample } from "../AttributeFormat/PointFormatSettings";
import { exampleDate } from "../AttributeFormat/DateFormatSettings";
import { updatePointFormat, updateDateFormat } from "./useUpdateAttributeFormat";
import { FormStateType, NumberFormState } from "./useAttributeFormat";
import { numberOptions } from "../AttributeFormat/FormatSettingsOptions";
import { formatAttributeValue } from "../../../../../utils/formatAttributeValue";

const getTextLabel = (mainLabel: string, additionalLabel: string) =>
  (additionalLabel && `${mainLabel} (${additionalLabel})`) || `${mainLabel}`;

export const useAttributeFormatLabel = (formState: FormStateType | NumberFormState, type: AttributeType) => {
  const { t } = useTranslation("common");
  const defaultLabel = useMemo(() => t("defaultValue"), [t]);
  const [formatLabel, setFormat] = useState(defaultLabel);

  const getNumberInputLabel = useCallback(() => {
    const { format, label } = formState as NumberFormState;
    const scalingOption = numberOptions.find(({ value }) => format === value);
    const scalingLabel = scalingOption && t(scalingOption.localizationKey);

    if (!scalingLabel) {
      return getTextLabel(defaultLabel, label);
    }

    return getTextLabel(scalingLabel, label);
  }, [defaultLabel, formState]);

  useEffect(() => {
    let currentFormatLabel = null;
    let labelWithformattedValue = null;

    switch (type) {
      case AttributeType.Point:
        labelWithformattedValue = formatAttributeValue(type, pointExample, updatePointFormat(formState));
        currentFormatLabel = labelWithformattedValue || defaultLabel;
        break;
      case AttributeType.DateTime:
        labelWithformattedValue = formatAttributeValue(type, exampleDate, updateDateFormat(formState));
        currentFormatLabel = labelWithformattedValue || defaultLabel;
        break;
      default:
        currentFormatLabel = getNumberInputLabel();
        break;
    }

    setFormat(currentFormatLabel);
  }, [type, formState, setFormat]);

  return formatLabel;
};
